import { useState, useEffect } from 'react';
import { ToggleSwitch } from '@/components/ui/toggle-switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUpdateForward } from '../../hooks';
import { PhoneForwarded, TriangleAlert } from 'lucide-react';
import type { DID } from '../../types';

interface ForwardTabProps {
  did?: DID | null;
}

export function ForwardTab({ did }: ForwardTabProps) {
  const [enabled, setEnabled] = useState(false);
  const [forwardNumber, setForwardNumber] = useState('');
  const updateForward = useUpdateForward();

  useEffect(() => {
    if (did) {
      setEnabled(!!did.has_forward);
      setForwardNumber(did.forward || '');
    }
  }, [did]);

  const handleSubmit = () => {
    if (!did) return;
    updateForward.mutate({
      tn: did.tn,
      enabled,
      forwardNumber: enabled ? forwardNumber : undefined,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="p-3 bg-amber-50 rounded-xl">
          <PhoneForwarded className="h-6 w-6 text-amber-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Call Forwarding</h3>
          <p className="text-sm text-slate-500 mt-1">
            Forward all incoming calls to another phone number.
          </p>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
        <TriangleAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Mutual Exclusivity</p>
          <p className="text-sm text-amber-700">
            Only one of the following services may be enabled at a time:{' '}
            <strong>Forward, Failover, eFax, and Conference</strong>
          </p>
        </div>
      </div>

      {/* Toggle Card */}
      <div className="bg-slate-50 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-slate-900">Call Forwarding</p>
            <p className="text-sm text-slate-500">
              {enabled ? 'Calls will be forwarded' : 'Forwarding is disabled'}
            </p>
          </div>
          <ToggleSwitch
            enabled={enabled}
            onChange={setEnabled}
          />
        </div>

        {enabled && (
          <div className="pt-4 border-t border-slate-200">
            <Input
              label="Forward to Number"
              placeholder="Enter 10-digit phone number"
              value={forwardNumber}
              onChange={(e) => setForwardNumber(e.target.value.replace(/\D/g, ''))}
              maxLength={10}
              hint="All calls to this DID will be forwarded to this number"
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-2">
        <Button
          onClick={handleSubmit}
          isLoading={updateForward.isPending}
          disabled={!did || (enabled && forwardNumber.length !== 10)}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
}
